import layout from './ToolBar.html?raw';
import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import ToolBarViewModelType from '@/ViewModels/Controls/ToolBarViewModel';
import Utilities from '@/Utility/Utilities';
import ResourceManager from '@/Management/ResourceManager';
import ToolbarKeyboardController from '@/Utility/ToolbarKeyboardController';
import {DisposeBehavior} from '#/DefaultViewExtensionInterfaces';

export default class ToolBar extends Control {

    private jQueryControl: JQuery;
    private onDispose: Array<Function>;
    private resizeTimeout: number;
    private keyboardController: ToolbarKeyboardController;

    private debouncedDetectOverflow: (menuButton: JQuery, sec: JQuery) => void;

    public constructor(private viewModel: ToolBarViewModelType, private element?: JQuery) {
        super(viewModel, element);

        this.onDispose = [];
        this.keyboardController = new ToolbarKeyboardController();
        this.debouncedDetectOverflow = _.debounce((menuButton, sec) => this.detectOverflow(menuButton, sec), 100);

        this.onWidgetCreated(this.onWidgetCreatedCallback.bind(this));
    }

    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.element || $('<div>');

        this.applyTemplateAsync(layout, this.jQueryControl, 1);
        this.widget(null);

        return this.jQueryControl;
    }

    private onWidgetCreatedCallback(widget: null): void {
        this.async(() => {
            // Tab-Einstieg fuer die Toolbar sicherstellen
            this.keyboardController.ensureSingleTabStop(this.getControl());

            let sec = this.getControl().find('.secondary').eq(0);
            let menuButton = this.getControl().find('div.secondary-overflow-menu-button').eq(0);

            if (menuButton.length === 0 || sec.length === 0) return;

            const moreActionsLabel = ResourceManager.getInstance().get('TOOLBAR_MORE_ACTIONS');

            this.applyBindings(menuButton, {
                locator: this.defineComputed(() => {
                    if (this.isDisposed || this.viewModel.isDisposed)
                        return null;
                    if(!Utilities.isNullOrUndefined(this.viewModel.locator()))
                        return this.viewModel.locator() + '.secondary-overflow-menu-button';
                    return null;
                }),

                tooltip: moreActionsLabel,

                // Ohne diese Auszeichnung wird der Button nur als Text (die Anzahl) vorgelesen und ist
                // per Tastatur nicht erreichbar (WCAG 2.1.1 / 4.1.2). show/hide pflegen aria-expanded.
                attr: {
                    role: 'button',
                    tabindex: '-1',
                    'aria-haspopup': 'true',
                    'aria-expanded': 'false',
                    'aria-label': moreActionsLabel
                }
            });

            let overlay = $("<div>").addClass('toolbar-overflow-items');

            let tooltip = menuButton.kendoTooltip({
                position: 'left',
                showOn: 'click',
                autoHide: false,
                content: overlay as any,
                show: (e) => {
                    let tooltip = e.sender;
                    sec.css("width", sec.width() + "px"); // Die Breite des Menüs soll solange nicht ändern

                    // Menü als geöffnet kennzeichnen
                    let tooltipWrapper = overlay.closest('.k-tooltip').addClass('toolbar-overflow-menu');
                    if (!Utilities.isNullOrUndefined(this.viewModel.technicalName)) {
                        tooltipWrapper.attr('data-technical-name', this.viewModel.technicalName);
                    }
                    sec.addClass('toolbar-overflow-menu-shown');
                    // für Screen-Reader: Menü ist geöffnet
                    menuButton.attr('aria-expanded', 'true');

                    // Inhalte einfügen
                    //Anstatt Elemente in dem Tooltip(Dropdown) zu verschieben, klonen wir sie,
                    //denn wenn Elemente verschoben werden, können sie nicht mehr richtig
                    //aus dem DOM entfernt werden.
                    const secondaryOverflow = this.getOverflownItems(sec);
                    const clonedSecondary = secondaryOverflow.clone(true);
                    clonedSecondary.appendTo(overlay);

                    secondaryOverflow.each((_index: number, element: HTMLElement) => {
                        $(element).addClass('toolbar-overflow-item-hidden');
                    });

                    // Geklonte Buttons im Popup wieder per Tastatur erreichbar machen und Roving-Tabindex initialisieren.
                    this.keyboardController.activateOverflowItems(overlay);

                    // Nun stimmt die horizontale Position des Popups ggf. nicht mehr, diese nun anpassen
                    let dx = $(".k-callout-e", tooltip.popup.wrapper).offset().left - menuButton.offset().left;
                    tooltip.popup.wrapper.css("margin-left", -dx + "px");

                    let popupYtop = tooltip.popup.wrapper.offset().top;
                    let popupHeight = tooltip.popup.element.outerHeight();
                    let popupYbottom = popupYtop + popupHeight;

                    let bodyHeight = $(document.body).height();

                    if (popupYbottom >= bodyHeight) {
                        tooltip.popup.wrapper.css("margin-top", (bodyHeight - popupYbottom) + "px");
                    }
                },
                hide: (e) => {
                    let tooltip = e.sender;
                    // beim Schließen von Tooltip, Objekte aus Overlay löschen
                    overlay.empty();
                    sec.removeClass('toolbar-overflow-menu-shown');
                    // für Screen-Reader: Menü ist geschlossen
                    menuButton.attr('aria-expanded', 'false');
                    // Fokus zurück auf den Öffner-Button.
                    this.keyboardController.focusItem(menuButton, 0);
                    sec.css("width", "");
                    tooltip.popup.wrapper.css("margin-left", "");
                    tooltip.popup.wrapper.css("margin-top", "");
                    sec.children().each(function () {
                        if ($(this).hasClass('toolbar-overflow-item-hidden')) {
                            $(this).removeClass('toolbar-overflow-item-hidden');
                        }
                    });
                    window.setTimeout(() => {
                        this.debouncedDetectOverflow(menuButton, sec);
                    }, 1);
                }
            }).data('kendoTooltip');

            this.onDispose.push(() => {
                tooltip.destroy();
            });

            this.addEventHandler('click', menuButton, () => {
                if (sec.hasClass('toolbar-overflow-menu-shown')) {
                    tooltip.hide();
                }
            });

            this.addEventHandler('click', overlay, () => {
                tooltip.hide();
            });

            this.keyboardController.setupOverflowNavigation(menuButton, overlay, () => tooltip.hide());

            const resizeObserver = new ResizeObserver(() => {
                if (tooltip != null && tooltip.popup != null && tooltip.popup.visible()) {
                    // Menü beim Resize schließen, da es sonst geöffnet bleibt und sich nicht neu berechnet.
                    tooltip.hide();
                    return;
                }

                this.debouncedDetectOverflow(menuButton, sec);
            });
            resizeObserver.observe(sec[0]);

            this.addEventHandler('resize', $(window), () => {
                this.debouncedDetectOverflow(menuButton, sec);

                clearTimeout(this.resizeTimeout);
                this.resizeTimeout = window.setTimeout(() => {
                    if (this.isDisposed !== true && tooltip != null && tooltip.popup != null && tooltip.popup.visible()) {
                        tooltip.hide();
                    }
                }, 250);
            });

            this.onDispose.push(() => {
                try {
                    resizeObserver.unobserve(sec[0]);
                }
                catch (e) {
                }
            });

            this.defineComputed(() => {
                if (this.viewModel.isDisposed)
                    return;

                // Abhängigkeiten zu Attributen herstellen, die den Platz ändern könnten
                let secondaryItems = this.viewModel.secondary();
                for (let i = 0; i < secondaryItems.length; i++) {
                    let item = secondaryItems[i];
                    if (this.viewModel.isLink(item)) {
                        item.isVisible();
                        if (item.label != null) {
                            item.label.isVisible();
                            item.label.caption();
                        }
                        if (item.image != null) {
                            item.image.isVisible();
                            item.image.source();
                        }
                    }
                    else if (this.viewModel.isSpacer(item)) {
                        item.isVisible();
                    }
                }

                if (tooltip != null && tooltip.popup != null && tooltip.popup.visible()) {
                    tooltip.hide();
                    return;
                }

                // Wenn dieses computed getriggert wird, dann vermessen wir die Inhalte und aktualisieren ggf. den Overflow-Marker
                this.debouncedDetectOverflow(menuButton, sec);
            });

        }, 501);


        this.applyBindings(this.jQueryControl, {
            controlName: 'toolbar',

            attr: {
                role: 'toolbar',
                'aria-orientation': 'horizontal'
            },

            loader: this.viewModel.isBusy,

            controlIdent: this.identifier,

            tooltip: this.viewModel.tooltip,

            invalid: this.viewModel.invalidationText,

            visible: this.viewModel.isVisible,

            styles: this.definePureComputed({
                read: () => {
                    let styles = ['default'];

                    return styles;
                }
            }),

            states: this.definePureComputed({
                read: () => {
                    let states = [];

                    if (this.viewModel.isRequired() === true) {
                        states.push('required');
                    }

                    return states;
                }
            })
        });

        this.keyboardController.setupToolbarNavigation(this.jQueryControl);
    }

    private detectOverflow(menuButton: JQuery, sec: JQuery) {
        try {
            if (this.isDisposed !== false) return;

            let overflownItems = this.getOverflownItems(sec);
            sec.children().removeClass('toolbar-overflowed');
            overflownItems.addClass('toolbar-overflowed');
            let overflownVisibleLinks = overflownItems.filter('.c-link:visible,.c-multilink:visible');
            this.viewModel.hasOverflowOnSecondaryItems(overflownVisibleLinks.length > 0);
            menuButton.text(overflownVisibleLinks.length);
            this.keyboardController.syncTabReachability(this.getControl(), menuButton);
        }
        catch (e) {

        }
    }

    private getOverflownItems($container: JQuery) {
        const containerWidth = $container.outerWidth();
        let children = $container.children().filter((i, e) => !$(e).hasClass('erd_scroll_detection_container') && ko.dataFor(e) != null);
        let visibleChildren = children.filter(':visible');
        if (visibleChildren.length === 0)
            return $([]);

        // RTL is not supported here

        const firstVisibleChild = visibleChildren.eq(0);
        let x = firstVisibleChild.offset().left - $container.offset().left;
        let xRight = x + firstVisibleChild.outerWidth();
        let from: number;
        if (xRight > containerWidth) {
            // Falls schon das erste Item nicht passt, dann brechen wir dieses manuell um, sodass es unsichtbar wird
            from = 0;
            firstVisibleChild.addClass('toolbar-leading-break');
        }
        else {
            // prüfen, ob das nächste Element noch passt
            firstVisibleChild.removeClass('toolbar-leading-break');
            xRight = firstVisibleChild.outerWidth(true);
            from = children.length;
            for (let i = 1; i < visibleChildren.length; i++) {
                xRight += visibleChildren.eq(i).outerWidth(true);
                if (xRight - containerWidth > 0.1) {
                    from = children.toArray().indexOf(visibleChildren[i]);
                    break;
                }
            }
        }
        return children.slice(from);
    }

    protected disposeInternal() : void {
        for (let i = this.onDispose.length - 1; i >= 0; i--) {
            this.onDispose[i]();
        }
        this.onDispose = [];

        super.disposeInternal();
    }

    protected getDisposeBehavior(): DisposeBehavior {
        return "Asynchronous";
    }
}

ComponentManager.setControl('ToolBar', ToolBar, 'RADPresentation.HTMLClient.DefaultViewExtensions');